import type { IFetcher, IFetchRequest, ILog, IQueryURL, Method } from '@monofe/util';
import type { IAuthenticateResponse } from '../interface/auth';

const HTTP_STATUS_BAD_REQUEST = 400;
const DEFAULT_TIMEOUT_MS = 20000; /* 2Os */

function _isIAuthenticateResponse(object: unknown): object is IAuthenticateResponse {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }
    const record: Partial<IAuthenticateResponse> = object as Partial<IAuthenticateResponse>;

    return (
        !isNullOrUndefined(record.access_token) &&
        !isNullOrUndefined(record.userName) &&
        !isNullOrUndefined(record.token_type) &&
        !isNullOrUndefined(record.expires_in) &&
        !isNullOrUndefined(record.refresh_token) &&
        !isNullOrUndefined(record['.expires']) &&
        !isNullOrUndefined(record['.issued'])
    );
}

function _request(
    query: IQueryURL,
    url: string,
    extension: string,
    clientId: string,
    clientSecret: string,
    grantType: string,
    username: string,
    password: string,
): IFetchRequest {
    const authParams = {
        /* eslint-disable-next-line camelcase */
        client_id: clientId,
        /* eslint-disable-next-line camelcase */
        client_secret: clientSecret,
        /* eslint-disable-next-line camelcase */
        grant_type: grantType,
        username,
        password,
    };

    const req: IFetchRequest = {
        url: url + extension,
        method: 'POST' as Method,
        body: query.assemble(authParams, false),
        headers: [{ name: 'Content-Type', value: 'application/x-www-form-urlencoded' }],
    };

    return req;
}

async function _authenticate(
    fetcher: IFetcher,
    logger: ILog,
    query: IQueryURL,
    authParams: IAuthParams,
    timeout: number,
    retryCount: number,
): Promise<IAuthenticateResponse> {
    const req = _request(
        query,
        authParams.apiUrl,
        authParams.apiExtension,
        authParams.clientId,
        authParams.clientSecret,
        authParams.grantType,
        authParams.username,
        authParams.password,
    );

    const response = await fetcher(req, timeout, retryCount).catch((error: Error) => {
        logger.error(
            `Authentication request failed: ${JSON.stringify(error)}, request:${JSON.stringify(
                req,
            )}`,
        );
        throw error;
    });

    if (response.statusCode === HTTP_STATUS_BAD_REQUEST) {
        throw new Error('Invalid credentials, authentication failed');
    } else if (!response.isStatusOk) {
        throw new Error(
            `Authenticate request failed: ${response.statusCode}, request:${JSON.stringify(req)}`,
        );
    } else if (!_isIAuthenticateResponse(response.payload)) {
        throw new Error('Missing access_token in auth response');
    }

    return response.payload;
}

export interface IAuthParams {
    apiUrl: string;
    apiExtension: string;
    clientId: string;
    clientSecret: string;
    grantType: string;
    username: string;
    password: string;
}

async function authenticate(
    fetchLoader: IFetcher,
    logger: ILog,
    query: IQueryURL,
    authParams: IAuthParams[],
    timeout = DEFAULT_TIMEOUT_MS,
    retryCount = DEFAULT_TIMEOUT_MS,
): Promise<IAuthenticateResponse> {
    const errors: unknown[] = [];

    logger.assert(
        authParams.length > 0,
        `Failed to find authentication params for apiservice:auth`,
    );

    for (const auth of authParams) {
        logger.assert(auth.clientId, `Bad clientId ${JSON.stringify(auth)}`);

        try {
            return await _authenticate(fetchLoader, logger, query, auth, timeout, retryCount);
        } catch (error) {
            errors.push(error);
        }
    }

    throw new Error(
        `Failed to authenticate: ${JSON.stringify(authParams)}, error:${JSON.stringify(errors)}`,
    );
}

export default authenticate;
